// src/components/Branch/modals/BranchDisplayCopyModal.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  XIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";
import { getBranchesThunk } from "../../../store/thunks/branchThunks";
import { copyDisplayConfigThunk } from "../../../store/thunks/branchDisplayThunks";

const COPY_SECTIONS = [
  { key: "branding", label: "Branding & Logo", description: "Display name, tagline, logo, banner, brand colors" },
  { key: "storeImages", label: "Store Images", description: "Gallery images and videos" },
  { key: "highlights", label: "Highlights & USPs", description: "Store highlights and unique selling points" },
  { key: "collections", label: "Collections", description: "Jewelry collection categories" },
  { key: "services", label: "Services", description: "Store services offered" },
  { key: "certifications", label: "Certifications & Awards", description: "Certifications and awards" },
  { key: "testimonials", label: "Testimonials", description: "Customer testimonials" },
  { key: "contactInfo", label: "Contact Info", description: "Contact details and social media" },
  { key: "teamMembers", label: "Team Members", description: "Team member profiles" },
  { key: "storeInfo", label: "Store Info", description: "Facilities, payment methods, directions" },
  { key: "seo", label: "SEO Settings", description: "Meta tags, keywords, structured data" },
  { key: "visibility", label: "Visibility Settings", description: "Public visibility and app settings" },
];

const BranchDisplayCopyModal = ({ isOpen, onClose, sourceBranch }) => {
  const dispatch = useDispatch();
  const { branches, loading: branchLoading } = useSelector((state) => state.branch);
  const { loading: copyLoading } = useSelector((state) => state.branchDisplay);

  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedSections, setSelectedSections] = useState(["branding"]);
  const [copyResult, setCopyResult] = useState(null);
  const [step, setStep] = useState(1); // 1: Select sections, 2: Select branches, 3: Confirm

  useEffect(() => {
    if (isOpen) {
      dispatch(getBranchesThunk({ isActive: true }));
      setSelectedBranches([]);
      setSelectedSections(["branding"]);
      setCopyResult(null);
      setStep(1);
    }
  }, [dispatch, isOpen]);

  // Filter out source branch
  const targetBranches = branches.filter(
    (b) => b.code !== sourceBranch?.code && b.isActive
  );

  const handleToggleSection = (sectionKey) => {
    setSelectedSections((prev) =>
      prev.includes(sectionKey)
        ? prev.filter((s) => s !== sectionKey)
        : [...prev, sectionKey]
    );
  };

  const handleToggleBranch = (branchCode) => {
    setSelectedBranches((prev) =>
      prev.includes(branchCode)
        ? prev.filter((b) => b !== branchCode)
        : [...prev, branchCode]
    );
  };

  const handleSelectAllBranches = () => {
    if (selectedBranches.length === targetBranches.length) {
      setSelectedBranches([]);
    } else {
      setSelectedBranches(targetBranches.map((b) => b.code));
    }
  };

  const handleSelectAllSections = () => {
    if (selectedSections.length === COPY_SECTIONS.length) {
      setSelectedSections([]);
    } else {
      setSelectedSections(COPY_SECTIONS.map((s) => s.key));
    }
  };

  const handleCopy = async () => {
    if (selectedBranches.length === 0) {
      alert("Please select at least one target branch");
      return;
    }

    if (selectedSections.length === 0) {
      alert("Please select at least one section to copy");
      return;
    }

    try {
      const result = await dispatch(
        copyDisplayConfigThunk({
          sourceBranchCode: sourceBranch.code,
          targetBranchCodes: selectedBranches,
          sections: selectedSections,
        })
      ).unwrap();

      setCopyResult({
        success: true,
        message: result.message || `Successfully copied to ${selectedBranches.length} branch(es)`,
        details: result.details,
      });
    } catch (error) {
      setCopyResult({
        success: false,
        message: error || "Failed to copy display configuration",
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <DocumentDuplicateIcon className="h-5 w-5 text-purple-600" />
            <h3 className="text-lg font-semibold">Copy Display Configuration</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Result Message */}
          {copyResult && (
            <div
              className={`mb-4 p-4 rounded-lg ${
                copyResult.success
                  ? "bg-green-50 border border-green-200"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <div className="flex items-start gap-2">
                {copyResult.success ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <ExclamationCircleIcon className="h-5 w-5 text-red-600 mt-0.5" />
                )}
                <div>
                  <p
                    className={`font-medium ${
                      copyResult.success ? "text-green-800" : "text-red-800"
                    }`}
                  >
                    {copyResult.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Source Branch Info */}
          <div className="bg-blue-50 p-3 rounded-lg mb-4">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Source:</span> {sourceBranch?.name} ({sourceBranch?.code})
            </p>
          </div>

          {/* Step 1: Select Sections */}
          {step === 1 && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium">Select sections to copy:</h4>
                <button
                  onClick={handleSelectAllSections}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {selectedSections.length === COPY_SECTIONS.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {COPY_SECTIONS.map((section) => (
                  <label
                    key={section.key}
                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                      selectedSections.includes(section.key)
                        ? "bg-purple-50 border-purple-300"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSections.includes(section.key)}
                      onChange={() => handleToggleSection(section.key)}
                      className="h-4 w-4 mt-0.5 rounded"
                    />
                    <div>
                      <p className="font-medium text-sm">{section.label}</p>
                      <p className="text-xs text-gray-500">{section.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Select Target Branches */}
          {step === 2 && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium">Select target branches:</h4>
                <button
                  onClick={handleSelectAllBranches}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {selectedBranches.length === targetBranches.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>

              {branchLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : targetBranches.length === 0 ? (
                <p className="text-center py-8 text-gray-500">
                  No other active branches available
                </p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {targetBranches.map((branch) => (
                    <label
                      key={branch._id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                        selectedBranches.includes(branch.code)
                          ? "bg-purple-50 border-purple-300"
                          : "bg-white border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedBranches.includes(branch.code)}
                        onChange={() => handleToggleBranch(branch.code)}
                        className="h-4 w-4 rounded"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{branch.name}</p>
                        <p className="text-xs text-gray-500 font-mono">{branch.code}</p>
                      </div>
                      <span className="text-xs text-gray-400">
                        {branch.address?.city}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This will overwrite the selected sections
                  in the target branches. This action cannot be undone.
                </p>
              </div>

              <div>
                <h4 className="font-medium mb-2">Summary:</h4>
                <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm">
                  <p>
                    <span className="text-gray-600">Sections:</span>{" "}
                    {selectedSections.length} selected
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selectedSections.map((key) => (
                      <span
                        key={key}
                        className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs"
                      >
                        {COPY_SECTIONS.find((s) => s.key === key)?.label}
                      </span>
                    ))}
                  </div>
                  <p className="mt-2">
                    <span className="text-gray-600">Target Branches:</span>{" "}
                    {selectedBranches.length} selected
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selectedBranches.map((code) => (
                      <span
                        key={code}
                        className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-mono"
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-4 border-t bg-gray-50">
          <div>
            {step > 1 && !copyResult?.success && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Back
              </button>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              {copyResult?.success ? "Close" : "Cancel"}
            </button>

            {!copyResult?.success && (
              <>
                {step < 3 ? (
                  <button
                    onClick={() => setStep(step + 1)}
                    disabled={
                      (step === 1 && selectedSections.length === 0) ||
                      (step === 2 && selectedBranches.length === 0)
                    }
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={handleCopy}
                    disabled={copyLoading}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 flex items-center gap-2"
                  >
                    {copyLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    )}
                    Copy Configuration
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchDisplayCopyModal;
